import type { ScheduleItem } from '../stores/scheduleStore';
import type { ColorSchema, FieldMapping, ItemLevel } from './board-config.api';
import {
  collectMappedColumnIds,
  fetchFilteredBoardItems,
  fetchSubitemsBoardInfo,
  fetchStatusColumnInfo,
  updateMondayItem,
  type MondayItem,
  type MondayItemColumnValue,
  type ItemsFilter,
  type StatusColorMap,
} from './monday.api';

// ─── Request / Response types ─────────────────────────────────────────────────

export interface CreateScheduleItemPayload {
  boardId: number;
  mondayItemId: number;
  assigneeId: number;
  startDate: string;
  endDate: string;
  hours: number;
}

export interface UpdateSchedulePayload {
  startDate?: string;
  endDate?: string;
  assigneeId?: number;
  hours?: number;
  name?: string;
}

export interface BulkMovePayload {
  itemIds: string[];
  daysDelta: number;
}

export interface BulkReassignPayload {
  itemIds: string[];
  targetUserId: number;
}

export interface UnplannedItem {
  id: number;
  name: string;
  groupName?: string;
  status?: string;
  color?: string;
}

// ─── Default item colors ──────────────────────────────────────────────────────

const ITEM_COLORS = [
  '#579bfc', '#bb3354', '#ff642e', '#fdab3d', '#00c875',
  '#a25ddc', '#037f4c', '#66ccff', '#784bd1', '#ff158a',
];

// ─── Column value parsers ─────────────────────────────────────────────────────

function parseTimeline(cv: MondayItemColumnValue): { from: string; to: string } | null {
  if (!cv.value) return null;
  try {
    const parsed = JSON.parse(cv.value) as { from?: string; to?: string };
    if (parsed.from && parsed.to) return { from: parsed.from, to: parsed.to };
  } catch { /* ignore */ }
  return null;
}

interface ParsedAssignees {
  personIds: number[];
  teamIds: number[];
}

function parseAssignees(cv: MondayItemColumnValue): ParsedAssignees {
  if (!cv.value) return { personIds: [], teamIds: [] };
  try {
    const parsed = JSON.parse(cv.value) as {
      personsAndTeams?: Array<{ id: number; kind: string }>;
    };
    const entries = parsed.personsAndTeams ?? [];
    return {
      personIds: entries.filter((p) => p.kind === 'person').map((p) => p.id),
      teamIds: entries.filter((p) => p.kind === 'team').map((p) => p.id),
    };
  } catch { /* ignore */ }
  return { personIds: [], teamIds: [] };
}

function parseColor(cv: MondayItemColumnValue): string | null {
  if (!cv.value) return null;
  try {
    const parsed = JSON.parse(cv.value) as { color?: string };
    return parsed.color ?? null;
  } catch { /* ignore */ }
  return null;
}

function parseStatusIndex(cv: MondayItemColumnValue): number | null {
  if (!cv.value) return null;
  try {
    const parsed = JSON.parse(cv.value) as { index?: number };
    return parsed.index ?? null;
  } catch { /* ignore */ }
  return null;
}

function parseHours(cv: MondayItemColumnValue): number {
  if (!cv.value && !cv.text) return 0;
  const text = cv.text || '';
  const num = parseFloat(text);
  return isNaN(num) ? 0 : num;
}

/**
 * Resolve the display color for a schedule item based on the colorSchema setting.
 */
function resolveColor(
  colorSchema: ColorSchema,
  groupColor: string | undefined,
  pickerColor: string | null,
  statusIndex: number | null,
  statusColorMap: StatusColorMap | null,
  fallbackIndex: number,
): string {
  switch (colorSchema) {
    case 'picker':
      return pickerColor ?? groupColor ?? ITEM_COLORS[fallbackIndex % ITEM_COLORS.length];
    case 'status': {
      const statusColor = statusIndex !== null && statusColorMap
        ? statusColorMap.get(statusIndex) ?? null
        : null;
      return statusColor ?? groupColor ?? ITEM_COLORS[fallbackIndex % ITEM_COLORS.length];
    }
    case 'group':
    default:
      return groupColor ?? pickerColor ?? ITEM_COLORS[fallbackIndex % ITEM_COLORS.length];
  }
}

// ─── Convert monday.com items to ScheduleItems ───────────────────────────────

/**
 * Convert raw monday.com board items into ScheduleItem[] using field mapping.
 * Creates one ScheduleItem per assignee (multi-assignee items become multiple rows).
 */
export function convertMondayItemsToScheduleItems(
  mondayItems: MondayItem[],
  fieldMapping: FieldMapping,
  boardId?: number,
  boardName?: string,
  colorSchema: ColorSchema = 'group',
  statusColorMap: StatusColorMap | null = null,
  doneIndices: Set<number> | null = null,
  /** When true, divide hours equally among assignees. When false, each assignee gets full hours. */
  perPersonDistribution = true,
): ScheduleItem[] {
  const items: ScheduleItem[] = [];

  for (const item of mondayItems) {
    let startDate: string | null = null;
    let endDate: string | null = null;
    let parsedAssignees: ParsedAssignees = { personIds: [], teamIds: [] };
    let color: string | null = null;
    let status: string | null = null;
    let statusIndex: number | null = null;
    let hoursTotal = 0;
    let hoursPerDay = 0;

    for (const cv of item.column_values) {
      if (fieldMapping.timelineColumnId && cv.id === fieldMapping.timelineColumnId) {
        const timeline = parseTimeline(cv);
        if (timeline) {
          startDate = timeline.from;
          endDate = timeline.to;
        }
      }

      if (fieldMapping.assigneesColumnId && cv.id === fieldMapping.assigneesColumnId) {
        parsedAssignees = parseAssignees(cv);
      }

      if (fieldMapping.colorColumnId && cv.id === fieldMapping.colorColumnId) {
        color = parseColor(cv);
      }

      if (fieldMapping.statusColumnId && cv.id === fieldMapping.statusColumnId) {
        status = cv.text || null;
        statusIndex = parseStatusIndex(cv);
      }

      if (fieldMapping.scheduledHoursColumnId && cv.id === fieldMapping.scheduledHoursColumnId) {
        hoursTotal = parseHours(cv);
      }

      if (fieldMapping.scheduledHoursPerDayColumnId && cv.id === fieldMapping.scheduledHoursPerDayColumnId) {
        hoursPerDay = parseHours(cv);
      }
    }

    // Skip items without timeline
    if (!startDate || !endDate) continue;

    // Resolve per-item work hour format: per-day wins when both are set
    const hasHoursConflict = hoursPerDay > 0 && hoursTotal > 0;
    const resolvedFormat: 'per_day' | 'total' = hoursPerDay > 0 ? 'per_day' : 'total';
    const resolvedHours = hoursPerDay > 0 ? hoursPerDay : hoursTotal;

    const { personIds, teamIds } = parsedAssignees;
    const groupColor = item.group?.color || undefined;
    const baseColor = resolveColor(colorSchema, groupColor, color, statusIndex, statusColorMap, Number(item.id));
    const statusColor = statusIndex !== null && statusColorMap
      ? statusColorMap.get(statusIndex) ?? undefined
      : undefined;
    const isDone = doneIndices !== null && statusIndex !== null && doneIndices.has(statusIndex);

    const hoursFields = {
      hoursPerDay,
      hoursTotal,
      workHourFormat: resolvedFormat,
      hasHoursConflict,
    };

    const groupName = item.group?.title || undefined;

    const totalAssignees = personIds.length + teamIds.length;

    if (personIds.length > 0 || teamIds.length > 0) {
      // Create one schedule item per person assignee
      for (const assigneeId of personIds) {
        const itemId = totalAssignees > 1
          ? `monday-${boardId}-${item.id}-${assigneeId}`
          : `monday-${boardId}-${item.id}`;

        const perPersonHours = (perPersonDistribution && totalAssignees > 1)
          ? Math.round((resolvedHours / totalAssignees) * 100) / 100
          : resolvedHours;

        items.push({
          id: itemId,
          mondayItemId: Number(item.id),
          name: item.name,
          assigneeId,
          startDate,
          endDate,
          hours: perPersonHours,
          ...hoursFields,
          color: baseColor,
          groupId: item.group?.id,
          groupName,
          groupColor,
          status: status ?? undefined,
          statusColor,
          isDone,
          boardId,
          boardName,
          originalTotalHours: resolvedHours,
          assigneeCount: totalAssignees,
        });
      }

      // Create one schedule item per team assignee
      for (const teamId of teamIds) {
        const itemId = totalAssignees > 1
          ? `monday-${boardId}-${item.id}-team-${teamId}`
          : `monday-${boardId}-${item.id}`;

        const perTeamHours = (perPersonDistribution && totalAssignees > 1)
          ? Math.round((resolvedHours / totalAssignees) * 100) / 100
          : resolvedHours;

        items.push({
          id: itemId,
          mondayItemId: Number(item.id),
          name: item.name,
          assigneeId: 0, // no specific person
          assignedTeamId: teamId,
          startDate,
          endDate,

          hours: perTeamHours,
          ...hoursFields,
          color: baseColor,
          groupId: item.group?.id,
          groupName,
          groupColor,
          status: status ?? undefined,
          statusColor,
          isDone,
          boardId,
          boardName,
          originalTotalHours: resolvedHours,
          assigneeCount: totalAssignees,
        });
      }
    } else {
      // No assignees at all — create a single unassigned item
      items.push({
        id: `monday-${boardId}-${item.id}`,
        mondayItemId: Number(item.id),
        name: item.name,
        assigneeId: 0,
        startDate,
        endDate,
        hours: resolvedHours,
        ...hoursFields,
        color: baseColor,
        groupId: item.group?.id,
        groupName,
        groupColor,
        status: status ?? undefined,
        statusColor,
        isDone,
        boardId,
        boardName,
        originalTotalHours: resolvedHours,
        assigneeCount: 1,
      });
    }
  }

  return items;
}

/**
 * Extract subitems from parent monday.com items and convert them to ScheduleItem[]
 * using the subitem-specific field mapping (columns on the subitems board).
 */
export function convertSubitemsToScheduleItems(
  parentItems: MondayItem[],
  subitemFieldMapping: FieldMapping,
  parentBoardId: number,
  subitemsBoardId: number,
  boardName?: string,
  colorSchema: ColorSchema = 'group',
  statusColorMap: StatusColorMap | null = null,
  doneIndices: Set<number> | null = null,
  perPersonDistribution = true,
): ScheduleItem[] {
  const items: ScheduleItem[] = [];

  for (const parent of parentItems) {
    if (!parent.subitems || parent.subitems.length === 0) continue;

    for (const sub of parent.subitems) {
      let startDate: string | null = null;
      let endDate: string | null = null;
      let parsedAssignees: ParsedAssignees = { personIds: [], teamIds: [] };
      let color: string | null = null;
      let status: string | null = null;
      let statusIndex: number | null = null;
      let hoursTotal = 0;
      let hoursPerDay = 0;

      for (const cv of sub.column_values) {
        if (subitemFieldMapping.timelineColumnId && cv.id === subitemFieldMapping.timelineColumnId) {
          const timeline = parseTimeline(cv);
          if (timeline) {
            startDate = timeline.from;
            endDate = timeline.to;
          }
        }
        if (subitemFieldMapping.assigneesColumnId && cv.id === subitemFieldMapping.assigneesColumnId) {
          parsedAssignees = parseAssignees(cv);
        }
        if (subitemFieldMapping.colorColumnId && cv.id === subitemFieldMapping.colorColumnId) {
          color = parseColor(cv);
        }
        if (subitemFieldMapping.statusColumnId && cv.id === subitemFieldMapping.statusColumnId) {
          status = cv.text || null;
          statusIndex = parseStatusIndex(cv);
        }
        if (subitemFieldMapping.scheduledHoursColumnId && cv.id === subitemFieldMapping.scheduledHoursColumnId) {
          hoursTotal = parseHours(cv);
        }
        if (subitemFieldMapping.scheduledHoursPerDayColumnId && cv.id === subitemFieldMapping.scheduledHoursPerDayColumnId) {
          hoursPerDay = parseHours(cv);
        }
      }

      if (!startDate || !endDate) continue;

      // Resolve per-item work hour format: per-day wins when both are set
      const hasHoursConflict = hoursPerDay > 0 && hoursTotal > 0;
      const resolvedFormat: 'per_day' | 'total' = hoursPerDay > 0 ? 'per_day' : 'total';
      const resolvedHours = hoursPerDay > 0 ? hoursPerDay : hoursTotal;

      const { personIds, teamIds } = parsedAssignees;
      const groupColor = parent.group?.color || undefined;
      const baseColor = resolveColor(colorSchema, groupColor, color, statusIndex, statusColorMap, Number(sub.id));
      const subStatusColor = statusIndex !== null && statusColorMap
        ? statusColorMap.get(statusIndex) ?? undefined
        : undefined;
      const isDone = doneIndices !== null && statusIndex !== null && doneIndices.has(statusIndex);

      const subitemBase = {
        mondayItemId: Number(sub.id),
        name: sub.name,
        hoursPerDay,
        hoursTotal,
        workHourFormat: resolvedFormat,
        hasHoursConflict,
        color: baseColor,
        groupId: parent.group?.id,
        groupName: parent.group?.title || undefined,
        groupColor,
        status: status ?? undefined,
        statusColor: subStatusColor,
        isDone,
        boardId: parentBoardId,
        boardName,
        isSubitem: true as const,
        parentItemId: Number(parent.id),
        subitemsBoardId,
      };

      const subTotalAssignees = personIds.length + teamIds.length;

      if (personIds.length > 0 || teamIds.length > 0) {
        for (const assigneeId of personIds) {
          const itemId = subTotalAssignees > 1
            ? `monday-sub-${parentBoardId}-${sub.id}-${assigneeId}`
            : `monday-sub-${parentBoardId}-${sub.id}`;
          const perPersonHours = (perPersonDistribution && subTotalAssignees > 1) ? Math.round((resolvedHours / subTotalAssignees) * 100) / 100 : resolvedHours;
          items.push({ id: itemId, assigneeId, startDate, endDate, hours: perPersonHours, ...subitemBase, originalTotalHours: resolvedHours, assigneeCount: subTotalAssignees });
        }
        for (const teamId of teamIds) {
          const itemId = subTotalAssignees > 1
            ? `monday-sub-${parentBoardId}-${sub.id}-team-${teamId}`
            : `monday-sub-${parentBoardId}-${sub.id}`;
          const perTeamHours = (perPersonDistribution && subTotalAssignees > 1) ? Math.round((resolvedHours / subTotalAssignees) * 100) / 100 : resolvedHours;
          items.push({ id: itemId, assigneeId: 0, assignedTeamId: teamId, startDate, endDate, hours: perTeamHours, ...subitemBase, originalTotalHours: resolvedHours, assigneeCount: subTotalAssignees });
        }
      } else {
        items.push({
          id: `monday-sub-${parentBoardId}-${sub.id}`,
          assigneeId: 0,
          startDate,
          endDate,
          hours: resolvedHours,
          ...subitemBase,
          originalTotalHours: resolvedHours,
          assigneeCount: 1,
        });
      }
    }
  }

  return items;
}

// ─── Error helpers ────────────────────────────────────────────────────────────

/**
 * Detects errors originating from the monday.com GraphQL API that are safe to
 * swallow for a single board (so the rest of the scheduler keeps rendering).
 *
 * The monday SDK throws plain errors whose `.message` is just "Graphql
 * validation errors" — the useful detail (column_not_found, bad column id,
 * etc.) only appears in `console.error`, not on the Error itself. So we also
 * treat generic "Graphql validation errors" from the SDK as recoverable at
 * this layer. Worst case: a genuinely different GraphQL error for one board is
 * silently skipped; acceptable trade-off vs. the whole scheduler crashing when
 * a single stored column id has drifted.
 */
export function isRecoverableMondayQueryError(err: unknown): boolean {
  if (!err) return false;
  const message = err instanceof Error ? err.message : String(err);
  if (/Column not found/i.test(message)) return true;
  if (/missing_column/i.test(message)) return true;
  if (/ResourceNotFoundException/i.test(message)) return true;
  if (/Graphql validation/i.test(message)) return true;
  return false;
}

// ─── Fetch schedule items from monday.com ─────────────────────────────────────

/**
 * Fetch board items from monday.com and convert to ScheduleItem[].
 * Respects itemLevel setting to include main items, sub-items, or both.
 * Uses separate field mappings for main items and sub-items.
 */
export async function fetchScheduleFromMonday(
  boardId: number,
  fieldMapping: FieldMapping,
  filter?: ItemsFilter,
  itemLevel: ItemLevel = 'main',
  subitemFieldMapping?: FieldMapping | null,
  colorSchema: ColorSchema = 'group',
  perPersonDistribution = true,
  /** Invoked when a partial failure occurs (e.g. subitem fetch fails but main
   *  items loaded). Callers can use this to flag the board as broken in the
   *  UI even when the function resolves with some items. */
  onPartialError?: (err: unknown) => void,
): Promise<ScheduleItem[]> {
  const apiFilter = filter ?? {};

  // Fetch status column settings when needed (for colors and/or done-state detection)
  let statusColorMap: StatusColorMap | null = null;
  let doneIndices: Set<number> | null = null;

  if (fieldMapping.statusColumnId) {
    const info = await fetchStatusColumnInfo(boardId, fieldMapping.statusColumnId).catch(() => null);
    if (info) {
      statusColorMap = info.colorMap;
      doneIndices = info.doneIndices;
    }
  }

  const result: ScheduleItem[] = [];

  // Fetch main items only when needed (skip for sub-only mode).
  // Also skip boards with no timelineColumnId — items without a timeline are
  // dropped by convertMondayItemsToScheduleItems anyway, so fetching them is
  // pure waste.
  let mondayItems: MondayItem[] = [];
  let boardName = '';
  if ((itemLevel === 'main' || itemLevel === 'both') && fieldMapping.timelineColumnId) {
    // Pass only the mapped column IDs to reduce complexity. Subitems are
    // fetched via a separate subitems-board query below, so disable the
    // nested subitems field here (was previously fetched but never read).
    // NOTE: do NOT swallow errors here — callers (useSchedule's
    // `fetchMultiBoardSchedule`) use Promise.allSettled per-board and record
    // broken boards based on rejections so the UI can flag them. Swallowing
    // the error would make a broken board indistinguishable from an empty one.
    const mainColumnIds = collectMappedColumnIds(fieldMapping);
    const fetched = await fetchFilteredBoardItems(boardId, apiFilter, undefined, false, mainColumnIds);
    mondayItems = fetched.items;
    boardName = fetched.boardName;
    result.push(...convertMondayItemsToScheduleItems(mondayItems, fieldMapping, boardId, boardName, colorSchema, statusColorMap, doneIndices, perPersonDistribution));
  }

  // Include sub-items when level is 'sub' or 'both'.
  // Skip if subitem mapping has no timelineColumnId — same waste reasoning as main items.
  if (
    (itemLevel === 'sub' || itemLevel === 'both')
    && subitemFieldMapping
    && subitemFieldMapping.timelineColumnId
  ) {
    try {
    // Query the subitems board directly with timeline filtering — much more
    // efficient than fetching all parent items and extracting nested subitems.
    const subBoardInfo = await fetchSubitemsBoardInfo(boardId);
    const subBoardId = subBoardInfo?.subitemsBoardId ?? 0;

    if (subBoardId) {
      // Build a filter for the subitems board using the subitem field mapping's timeline column
      const subFilter: ItemsFilter = {};
      if (filter?.timeline && subitemFieldMapping.timelineColumnId) {
        subFilter.timeline = {
          columnId: subitemFieldMapping.timelineColumnId,
          startDate: filter.timeline.startDate,
          endDate: filter.timeline.endDate,
        };
      }

      // Fetch subitems directly from the subitems board (no need for parent nesting).
      // Pass only the mapped columns from subitemFieldMapping to reduce complexity.
      const subMappedColumnIds = collectMappedColumnIds(subitemFieldMapping);
      const { items: subMondayItems, boardName: subBoardName } = await fetchFilteredBoardItems(
        subBoardId,
        subFilter,
        100,
        false,
        subMappedColumnIds,
      );

      // If main fetch was skipped (sub-only mode), derive the parent board name
      // from the subitems board name (monday.com names it "Subitems of {Board Name}")
      if (!boardName && subBoardName) {
        boardName = subBoardName.replace(/^Subitems of /i, '') || subBoardName;
      }

      // If subitems have their own status column, fetch its settings
      let subStatusColorMap = statusColorMap;
      let subDoneIndices = doneIndices;
      if (subitemFieldMapping.statusColumnId) {
        const subInfo = await fetchStatusColumnInfo(subBoardId, subitemFieldMapping.statusColumnId).catch(() => null);
        if (subInfo) {
          subStatusColorMap = subInfo.colorMap;
          subDoneIndices = subInfo.doneIndices;
        }
      }

      // Override each subitem's group with its parent's group color
      // (fetched via parent_item { id group { id title color } })
      for (const mi of subMondayItems) {
        if (mi.parent_item?.group) {
          mi.group = mi.parent_item.group;
        }
      }

      const subItems = convertMondayItemsToScheduleItems(
        subMondayItems, subitemFieldMapping, boardId, boardName,
        colorSchema, subStatusColorMap, subDoneIndices, perPersonDistribution,
      );

      // Mark as subitems, set subitemsBoardId, parentItemId, and parentItemName
      for (const item of subItems) {
        item.isSubitem = true;
        item.subitemsBoardId = subBoardId;
        const subMondayItem = subMondayItems.find((mi) => Number(mi.id) === item.mondayItemId);
        if (subMondayItem?.parent_item) {
          item.parentItemId = Number(subMondayItem.parent_item.id);
          item.parentItemName = subMondayItem.parent_item.name;
        }
      }

      result.push(...subItems);
    }
    } catch (err) {
      if (!isRecoverableMondayQueryError(err)) throw err;
      // Subitem timeline column was deleted/renamed on monday. Skip subitems
      // rather than crashing the scheduler, but notify the caller so it can
      // flag the board as broken in the UI.
      console.warn('[schedule] Skipping subitems: mapped subitem timeline column missing', { boardId, subitemTimelineColumnId: subitemFieldMapping.timelineColumnId });
      onPartialError?.(err);
    }
  }

  return result;
}

// ─── Write-back to monday.com ─────────────────────────────────────────────────

/**
 * Update a timeline column on a monday.com item.
 */
export async function updateItemTimeline(
  boardId: number,
  itemId: number,
  timelineColumnId: string,
  startDate: string,
  endDate: string,
): Promise<void> {
  const value = JSON.stringify({ from: startDate, to: endDate });
  await updateMondayItem(boardId, itemId, timelineColumnId, value);
}

/**
 * Update the people column on a monday.com item.
 * Supports both person and team assignments via the `kind` parameter.
 *
 * Overload 1: uniform kind for all IDs (backward-compatible).
 * Overload 2: mixed entries with per-entry kind.
 */
export async function updateItemAssignee(
  boardId: number,
  itemId: number,
  peopleColumnId: string,
  assigneeIds: number[],
  kind?: 'person' | 'team',
): Promise<void>;
export async function updateItemAssignee(
  boardId: number,
  itemId: number,
  peopleColumnId: string,
  entries: Array<{ id: number; kind: 'person' | 'team' }>,
): Promise<void>;
export async function updateItemAssignee(
  boardId: number,
  itemId: number,
  peopleColumnId: string,
  assigneesOrEntries: number[] | Array<{ id: number; kind: 'person' | 'team' }>,
  kind: 'person' | 'team' = 'person',
): Promise<void> {
  let personsAndTeams: Array<{ id: number; kind: string }>;

  if (assigneesOrEntries.length > 0 && typeof assigneesOrEntries[0] === 'object' && 'kind' in assigneesOrEntries[0]) {
    // Mixed entries format
    personsAndTeams = (assigneesOrEntries as Array<{ id: number; kind: 'person' | 'team' }>).map(
      (e) => ({ id: e.id, kind: e.kind }),
    );
  } else {
    // Uniform kind format (backward-compatible)
    personsAndTeams = (assigneesOrEntries as number[]).map((id) => ({ id, kind }));
  }

  const value = JSON.stringify({ personsAndTeams });
  await updateMondayItem(boardId, itemId, peopleColumnId, value);
}

// Re-export for external consumers
export { fetchFilteredBoardItems, type ItemsFilter } from './monday.api';
